/**
 * To prevent circular dependencies, please refrain from importing from requestSelectors.ts and try to keep an object oriented format for your selectors
 */
import { createSelector } from 'reselect';
import {
  IContactUsState,
  IRequestDetailsState,
  AddNoteConfig,
} from '../../../../models/reduxState';
import { constructUserPhone, getEmailFromCustomerContacts } from '../../../../utilities';
import {
  contactStateSelector,
} from '../../../app/selectors/contactUsSelector';
import {
  AttachmentData,
  FreelanceRequest,
  Note,
} from '../../models/requestModels';
import { pristineRequestSelector } from './requestHistorySelectors';
import { getString } from '../../../../../web/utils/stringLoader';

export const requestDetailsSelector = createSelector(
  contactStateSelector,
  (r: IContactUsState) => r.RequestDetailsState
);

export const dirtyRequestSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.dirtyRequest
);

export const isUpdatingRequestSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.isUpdatingContacts || r.isAddingNote || r.isUpdatingAttachments
);

export const isGetRequestPendingSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.isGetRequestPending
);

export const businessImpactConfirmedSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.businessImpactConfirmed
);

export const customerAvailabilityConfirmed = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.customerAvailabilityConfirmed
);

export const addNoteConfigSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.addNoteConfig
);

export const isUpdateAttachmentsErrorSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.isUpdateAttachmentsError
);

export const isAddNoteErrorSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.isAddNoteError
);

export const isUpdateContactsErrorSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.isUpdateContactsError
);

export const isRequestDetailsEditErrorSelector = createSelector(
  [isUpdateAttachmentsErrorSelector, isAddNoteErrorSelector, isUpdateContactsErrorSelector],
  (isUpdateAttachmentsError: boolean, isAddNoteError: boolean, isUpdateContactsError: boolean) => isUpdateAttachmentsError && isAddNoteError && isUpdateContactsError
);

const getErrorList = (
  isRequestDetailsEditError: boolean,
  isUpdateAttachmentsError: boolean,
  isAddNoteError: boolean,
  isUpdateContactsError: boolean
): string[] => {
  const errorList = [];
  if (!!isRequestDetailsEditError) {
    errorList.push(getString('requestDetails_updateRequestDetailsError'));
    return errorList;
  }
  if (!!isUpdateAttachmentsError) {
    errorList.push(getString('requestDetails_updateAttachmentsError'));
  }
  if (!!isAddNoteError) {
    errorList.push(getString('requestDetails_addNoteError'));
  }
  if (!!isUpdateContactsError) {
    errorList.push(getString('requestDetails_updateContactsError'));
  }
  return errorList;
};

export const errorListSelector = createSelector(
  [isRequestDetailsEditErrorSelector, isUpdateAttachmentsErrorSelector, isAddNoteErrorSelector, isUpdateContactsErrorSelector],
  getErrorList
);

export const showErrorSelector = createSelector(
  [isRequestDetailsEditErrorSelector, isUpdateAttachmentsErrorSelector, isAddNoteErrorSelector, isUpdateContactsErrorSelector],
  (isRequestDetailsEditError: boolean, isUpdateAttachmentsError: boolean, isAddNoteError: boolean, isUpdateContactsError: boolean) => !!isRequestDetailsEditError || !!(isUpdateAttachmentsError || isAddNoteError || isUpdateContactsError)
);

const getAttachments = (
  request: FreelanceRequest
): AttachmentData[] => {
  let attachments;
  if (!!request) {
    attachments = request.Attachments;
  }
  return attachments;
};

const getIsAttachmentsUpdated = (
  pristineRequest: FreelanceRequest,
  dirtyRequest: FreelanceRequest
) => {
  const dirtyAttachments: AttachmentData[] = getAttachments(dirtyRequest);
  const pristineAttachments: AttachmentData[] = getAttachments(pristineRequest);
  // IsAttachmentsUpdated Scenarios:
  // 1. Return true of DirtyAttachments is [] but PristineAttachments has values.
  // 2. Return false if both DirtyAttachments and PristineAttachments are [].
  // 3. Return true if DirtyAttachments has values but PristineAttachments is [].
  // 4. Return true if DirtyAttachments and PristineAttachments has values, but they are not the same.
  // 5. Return false if DirtyAttachments and PristineAttachments has values, and these two are the same.
  return (
    !(dirtyAttachments && dirtyAttachments.length)
      ? !!(pristineAttachments && pristineAttachments.length)
      : !(!!pristineAttachments &&
        pristineAttachments.length === dirtyAttachments.length &&
        dirtyAttachments.every(
          (att: AttachmentData, idx: number) => att.CwsId === pristineAttachments[idx].CwsId)
      )
  );
};

export const isAttachmentsUpdatedSelector = createSelector(
  [pristineRequestSelector, dirtyRequestSelector],
  getIsAttachmentsUpdated
);

const getNotes = (
  request: FreelanceRequest
): Note[] => {
  let notes;
  if (!!request) {
    notes = request.Notes;
  }
  return notes;
};

const getAreNotesValid = (
  pristineRequest: FreelanceRequest,
  dirtyRequest: FreelanceRequest,
  addnoteConfig: AddNoteConfig
): boolean => {
  const arePristinNotesValid: boolean = !!getNotes(pristineRequest);
  const areDirtyNotesValid: boolean = !!getNotes(dirtyRequest);
  const pristineNotes: Note[] = arePristinNotesValid ? getNotes(pristineRequest): [];
  const dirtyNotes: Note[] = areDirtyNotesValid ? getNotes(dirtyRequest): [];
  const pristineLen: number = pristineNotes.length;
  const dirtyLen: number = dirtyNotes.length;
  const maxAllowedNotesCount: number = addnoteConfig ? addnoteConfig.MaxAllowedNotesCount : 6;
  const addNoteTextFieldCharLimit: number = addnoteConfig ? addnoteConfig.AddNoteTextFieldCharLimit: 500;
  return (
    !!dirtyNotes &&
    dirtyLen > 1 &&
    dirtyLen <= maxAllowedNotesCount &&
    (!!dirtyNotes[dirtyLen-1] &&
      !!dirtyNotes[dirtyLen-1].Text &&
      dirtyNotes[dirtyLen-1].Text.length > 0 &&
      dirtyNotes[dirtyLen-1].Text.length <= addNoteTextFieldCharLimit &&
      dirtyLen > pristineLen)
  );
};

export const areNotesValidSelector = createSelector(
  [pristineRequestSelector, dirtyRequestSelector, addNoteConfigSelector],
  getAreNotesValid
);

const getIsContactsUpdated = (
  pristineRequest: FreelanceRequest,
  dirtyRequest: FreelanceRequest
) => {
  const dirtyUserPhone: string = constructUserPhone(dirtyRequest) || null;
  const pristineUserEmail: string = getEmailFromCustomerContacts(pristineRequest) || null;
  return (
    !!pristineRequest &&
    !!dirtyRequest &&
    (dirtyRequest.UserEmail !== pristineUserEmail ||
      dirtyUserPhone !== pristineRequest.UserPhone ||
      // placeholder TwoDigitCountryCode will be set by default, we only want to enable save button if there is dirty userPhone
      !!dirtyUserPhone && dirtyRequest.TwoDigitCountryCode !== pristineRequest.TwoDigitCountryCode)
  );
};

/**
 * This is used for edit request in RequestEdit only, and will not be used in RequestCreation.
 * So we don't need to care about draftRequest in RequestCreation.
 */
export const isContactsUpdatedSelector = createSelector(
  [pristineRequestSelector, dirtyRequestSelector],
  getIsContactsUpdated
);

export const isC2CPhoneEditingEnabledSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.isC2CPhoneEditingEnabled
);

export const isRequestDetailsPhoneEditEnabledSelector = createSelector(
  [requestDetailsSelector],
  (r: IRequestDetailsState) => r.isRequestDetailsPhoneEditEnabled
);
